/**
 * Terms of Service — /terms route (PUBLIC, unauthenticated).
 *
 * Long-form legal terms governing use of the Sahas Forge platform operated by
 * Sahas Labs Pvt Ltd. Self-contained route, no marketing chrome / pack-gated
 * providers — mirrors the readable-article layout (centered max-width column,
 * BrandMark lockup, back-to-home link) and is token-only Tailwind so it
 * inherits light/dark + tenant theming and prints cleanly via index.css print
 * styles.
 *
 * Tailored to an industrial early-access B2B SaaS: prominent "not a safety or
 * control system" clause, "AS IS / AS AVAILABLE" disclaimers, a liability cap,
 * customer-data ownership + licence, and India governing law.
 */

import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, ShieldAlert } from "lucide-react";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { useTenantTheme } from "@/lib/tenant-theme";

const EFFECTIVE_DATE = "10 June 2026";
const CONTACT_EMAIL = "info@sahaslabs.com";
const LIABILITY_CAP = "INR 10,000";

interface TocEntry {
  id: string;
  label: string;
}

const TOC: readonly TocEntry[] = [
  { id: "agreement", label: "1. Agreement to terms" },
  { id: "definitions", label: "2. Definitions" },
  { id: "eligibility", label: "3. Eligibility, accounts & approval" },
  { id: "service", label: "4. The Service & early-access nature" },
  { id: "acceptable-use", label: "5. Acceptable use" },
  { id: "not-a-safety-system", label: "6. Not a safety or control system" },
  { id: "customer-data", label: "7. Customer data & ownership" },
  { id: "intellectual-property", label: "8. Intellectual property" },
  { id: "fees", label: "9. Fees" },
  { id: "third-party", label: "10. Third-party services & devices" },
  { id: "confidentiality", label: "11. Confidentiality" },
  { id: "disclaimers", label: "12. Disclaimers" },
  { id: "liability", label: "13. Limitation of liability" },
  { id: "indemnification", label: "14. Indemnification" },
  { id: "termination", label: "15. Term, suspension & termination" },
  { id: "changes", label: "16. Changes to the Service & these Terms" },
  { id: "governing-law", label: "17. Governing law & dispute resolution" },
  { id: "force-majeure", label: "18. Force majeure" },
  { id: "general", label: "19. General" },
  { id: "contact", label: "20. Contact" },
];

/** A titled terms section with a stable anchor id for the table of contents. */
function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      data-pdf-block
      className="flex scroll-mt-24 flex-col gap-3"
    >
      <h2 className="text-xl font-semibold tracking-tight text-on-surface md:text-2xl">
        {title}
      </h2>
      <div className="flex flex-col gap-3 text-sm leading-relaxed text-on-surface-muted md:text-base">
        {children}
      </div>
    </section>
  );
}

/** In-page table of contents — anchor links to each section. */
function TableOfContents() {
  return (
    <nav
      aria-label="On this page"
      data-no-print
      className="rounded-xl border border-border bg-surface-elevated/60 p-5"
    >
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-on-surface-muted">
        On this page
      </p>
      <ol className="grid gap-1.5 sm:grid-cols-2">
        {TOC.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              className="text-sm text-on-surface-muted transition-colors hover:text-brand hover:underline"
            >
              {entry.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function TermsOfService() {
  const tenant = useTenantTheme();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-surface text-on-surface">
      <header
        data-no-print
        className="flex h-16 items-center justify-between border-b border-border px-4 md:px-10"
      >
        <Link to="/" className="flex items-center" aria-label={tenant.productName}>
          <BrandMark variant="full" className="h-9 md:h-10" />
        </Link>
        <Button variant="ghost" asChild>
          <Link to="/">
            <ArrowLeft className="mr-1.5 size-4" />
            Back to home
          </Link>
        </Button>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-8 px-4 py-12 md:py-16">
        {/* Title block */}
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">
            Legal
          </p>
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-on-surface md:text-4xl">
            Terms of Service
          </h1>
          <p className="text-sm text-on-surface-muted">
            Last updated: {EFFECTIVE_DATE} · Effective date: {EFFECTIVE_DATE}
          </p>
          <p className="max-w-2xl text-sm leading-relaxed text-on-surface-muted md:text-base">
            These Terms of Service govern your access to and use of the Sahas
            Forge platform and related services provided by Sahas Labs Pvt Ltd.
            Please read them carefully, together with our{" "}
            <Link
              to="/privacy"
              className="font-medium text-brand hover:underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
          <Button
            variant="outline"
            size="sm"
            data-no-print
            className="w-fit"
            onClick={() => window.print()}
          >
            <Printer className="mr-1.5 size-4" />
            Print / save as PDF
          </Button>
        </div>

        <TableOfContents />

        <div className="flex flex-col gap-10">
          <Section id="agreement" title="1. Agreement to terms">
            <p>
              These Terms of Service (the &ldquo;Terms&rdquo;) constitute a
              legally binding agreement between you and Sahas Labs Pvt Ltd
              (&ldquo;Sahas Labs&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
              &ldquo;our&rdquo;) and govern your access to and use of the Sahas
              Forge platform, our website, our application-programming interfaces,
              and any related software, tools, and services that link to or
              reference these Terms (collectively, the &ldquo;Service&rdquo;).
            </p>
            <p>
              By accessing or using the Service, by creating an account, or by
              clicking to accept these Terms, you acknowledge that you have read,
              understood, and agree to be bound by these Terms and by our Privacy
              Policy, which is incorporated by reference. If you do not agree to
              these Terms, you must not access or use the Service. If you are
              using the Service on behalf of an organisation, you represent and
              warrant that you are authorised to bind that organisation to these
              Terms, and in that case &ldquo;you&rdquo; refers to both you and
              that organisation.
            </p>
          </Section>

          <Section id="definitions" title="2. Definitions">
            <p>In these Terms:</p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                <span className="font-medium text-on-surface">
                  &ldquo;Service&rdquo; or &ldquo;Platform&rdquo;
                </span>{" "}
                means the Sahas Forge software-as-a-service platform and all
                related websites, software, interfaces, tooling, and services
                made available by Sahas Labs.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  &ldquo;Customer&rdquo;
                </span>{" "}
                means the organisation or individual that registers for, is
                approved to use, and accesses the Service.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  &ldquo;User&rdquo;
                </span>{" "}
                means any individual who accesses or uses the Service under a
                Customer&rsquo;s account, including the Customer&rsquo;s
                authorised personnel.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  &ldquo;Customer Data&rdquo;
                </span>{" "}
                means all data, content, and materials that a Customer or its
                Users submit to, upload to, or generate within the Service,
                including equipment models, process parameters, telemetry and
                sensor readings, batches and material genealogy, reports, and
                uploaded files.
              </li>
              <li>
                <span className="font-medium text-on-surface">
                  &ldquo;Account&rdquo;
                </span>{" "}
                means the credentials and workspace through which a Customer and
                its Users access the Service.
              </li>
            </ul>
          </Section>

          <Section id="eligibility" title="3. Eligibility, accounts & approval">
            <p>
              To use the Service, you must be at least eighteen (18) years of age
              and have the legal capacity and authority to enter into these
              Terms. Access to the Service is not granted automatically: each
              registration is subject to review and approval by Sahas Labs, which
              we may grant, decline, condition, or limit at our discretion. We may
              decline, suspend, or revoke access at any time, including where we
              consider that a registration is incomplete, inaccurate, or presents
              a security, legal, or business risk.
            </p>
            <p>
              You agree to provide accurate, current, and complete registration
              information, and to keep it up to date. You are responsible for
              maintaining the confidentiality and security of your account
              credentials, for all activity that occurs under your Account, and
              for ensuring that your Users comply with these Terms. You must
              notify us promptly at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {CONTACT_EMAIL}
              </a>{" "}
              of any unauthorised use of your Account or any other breach of
              security. Sahas Labs is not liable for any loss or damage arising
              from your failure to safeguard your credentials.
            </p>
          </Section>

          <Section id="service" title="4. The Service & early-access nature">
            <p>
              The Service is an industrial IoT and digital-twin platform that
              enables Customers to connect equipment, model processes as digital
              twins, monitor live and historical telemetry, trace material
              genealogy, and analyse quality and process data. The specific
              features available to you may depend on your plan, entitlements, and
              configuration.
            </p>
            <p>
              The Service is currently offered on an early-access / beta basis and
              is under active and ongoing development. Features may be added,
              changed, suspended, deprecated, or removed at any time, with or
              without notice. We do not warrant that the Service will be
              uninterrupted, timely, error-free, or continuously available, and we
              may impose usage limits, perform maintenance, or modify or
              discontinue all or part of the Service. You acknowledge that the
              early-access Service may contain defects or inaccuracies and should
              be used accordingly.
            </p>
          </Section>

          <Section id="acceptable-use" title="5. Acceptable use">
            <p>You agree that you will not, and will not permit any User to:</p>
            <ul className="flex list-disc flex-col gap-2 pl-5">
              <li>
                use the Service in violation of any applicable law, regulation, or
                third-party right;
              </li>
              <li>
                access or attempt to access the Service, any account, or any data
                without authorisation, or circumvent, disable, or interfere with
                any authentication, security, or access-control mechanism;
              </li>
              <li>
                scrape, harvest, crawl, or systematically extract data from the
                Service, except through interfaces and on terms we expressly
                permit;
              </li>
              <li>
                reverse-engineer, decompile, disassemble, or otherwise attempt to
                derive the source code, structure, or underlying ideas of the
                Service, except to the extent such restriction is prohibited by
                applicable law;
              </li>
              <li>
                introduce malware or malicious code, or take any action that
                imposes an unreasonable or disproportionately large load on, or
                otherwise overloads, impairs, or disrupts, the Service or its
                infrastructure;
              </li>
              <li>
                upload, store, or transmit content that is unlawful, infringing,
                defamatory, harmful, or that you do not have the right to submit;
                or
              </li>
              <li>
                use the Service to build or assist in building a competing product
                or service, or otherwise outside the scope of these Terms.
              </li>
            </ul>
          </Section>

          <Section
            id="not-a-safety-system"
            title="6. Not a safety or control system"
          >
            <div
              data-pdf-block
              className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-5"
            >
              <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-on-surface">
                <ShieldAlert className="size-4 shrink-0 text-warning" />
                Important — please read carefully
              </p>
              <p className="text-sm leading-relaxed text-on-surface">
                Sahas Forge is a monitoring, analytics, visualization, and
                digital-twin / record-keeping platform. It is{" "}
                <span className="font-semibold">NOT</span> a real-time control
                system, and it is <span className="font-semibold">NOT</span>{" "}
                certified, designed, or intended for safety-critical,
                life-critical, or hazardous-process control, nor for any
                application where failure, error, delay, or inaccuracy could lead
                to injury, loss of life, or property or environmental damage.
              </p>
              <p className="text-sm leading-relaxed text-on-surface">
                The Customer remains solely and fully responsible for its physical
                plant, equipment, personnel, process safety, and regulatory
                compliance, and for installing, operating, and maintaining
                independent and appropriately-rated control, protection, alarm,
                interlock, and safety systems. You must not rely on the Service
                for emergency response, for real-time control, or to prevent or
                mitigate injury, death, equipment damage, or environmental harm.
              </p>
              <p className="text-sm leading-relaxed text-on-surface">
                Data presented by the Service may be simulated, derived,
                estimated, aggregated, delayed, incomplete, or inaccurate, and
                must be independently verified before being relied upon for any
                operational, engineering, quality, or business decision. To the
                maximum extent permitted by law, Sahas Labs disclaims all
                liability arising from any use of the Service as, or in place of,
                a control or safety system.
              </p>
            </div>
          </Section>

          <Section id="customer-data" title="7. Customer data & ownership">
            <p>
              As between the parties, the Customer owns all right, title, and
              interest in and to its Customer Data. We do not claim ownership of
              Customer Data. You grant Sahas Labs a worldwide, non-exclusive,
              royalty-free, limited licence to host, store, process, transmit,
              reproduce, display, and otherwise use Customer Data solely as
              necessary to provide, secure, maintain, and improve the Service, to
              prevent or address technical or security issues, and as otherwise
              permitted under these Terms and our Privacy Policy.
            </p>
            <p>
              You are responsible for your Customer Data, including its legality,
              accuracy, quality, and integrity, and for ensuring that you have all
              rights, permissions, and consents necessary to submit it to the
              Service and to grant the licence above. You are responsible for
              maintaining your own backups of Customer Data; while we take
              reasonable measures to protect data, you should not rely on the
              Service as your sole repository or backup.
            </p>
          </Section>

          <Section id="intellectual-property" title="8. Intellectual property">
            <p>
              The Service, including all software, source code, designs, user
              interfaces, documentation, content (other than Customer Data),
              trademarks, logos, and all related intellectual-property rights, is
              and remains the exclusive property of Sahas Labs and its licensors.
              Subject to your compliance with these Terms, Sahas Labs grants you a
              limited, revocable, non-exclusive, non-transferable,
              non-sublicensable licence to access and use the Service for your
              internal business purposes during the term of these Terms. No rights
              are granted except as expressly set out herein.
            </p>
            <p>
              If you provide us with feedback, suggestions, or ideas about the
              Service, you grant Sahas Labs a perpetual, irrevocable, worldwide,
              royalty-free licence to use and incorporate that feedback into the
              Service without any obligation or compensation to you.
            </p>
          </Section>

          <Section id="fees" title="9. Fees">
            <p>
              The Service, or certain features of it, may be made available free
              of charge during the early-access period. We reserve the right to
              introduce fees for the Service or for particular features in the
              future, and to change our pricing, by giving reasonable prior
              notice. Where fees apply, they are payable in accordance with the
              applicable order or plan, and are exclusive of taxes, levies, and
              duties, which are your responsibility unless stated otherwise.
            </p>
          </Section>

          <Section id="third-party" title="10. Third-party services & devices">
            <p>
              The Service may interoperate with, or depend on, third-party
              products, services, networks, and hardware — including gateways,
              sensors, programmable logic controllers, connectivity, and
              infrastructure or platform providers such as Amazon Web Services and
              Google. Your use of any third-party product or service is governed
              by that third party&rsquo;s own terms and policies, and Sahas Labs
              is not responsible or liable for third-party products or services,
              or for your hardware, devices, networks, or connectivity. You are
              responsible for procuring, configuring, securing, and maintaining
              the equipment and connectivity required to use the Service.
            </p>
          </Section>

          <Section id="confidentiality" title="11. Confidentiality">
            <p>
              Each party (the &ldquo;Receiving Party&rdquo;) may have access to
              non-public information of the other party (the &ldquo;Disclosing
              Party&rdquo;) that is designated as confidential or that should
              reasonably be understood to be confidential given its nature and the
              circumstances of disclosure (&ldquo;Confidential
              Information&rdquo;). The Receiving Party will use the Disclosing
              Party&rsquo;s Confidential Information only as necessary to exercise
              its rights and perform its obligations under these Terms, will
              protect it using at least a reasonable standard of care, and will
              not disclose it to third parties except to those who need to know it
              and are bound by confidentiality obligations no less protective than
              these. These obligations do not apply to information that is or
              becomes public through no fault of the Receiving Party, is lawfully
              obtained from another source, or is required to be disclosed by law.
            </p>
          </Section>

          <Section id="disclaimers" title="12. Disclaimers">
            <p>
              To the maximum extent permitted by applicable law, the Service is
              provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo;
              basis, with all faults and without warranties of any kind. Sahas
              Labs expressly disclaims all warranties, conditions, and
              representations, whether express, implied, statutory, or otherwise,
              including any implied warranties or conditions of merchantability,
              fitness for a particular purpose, title, non-infringement, accuracy,
              reliability, and any warranties arising out of course of dealing or
              usage of trade.
            </p>
            <p>
              Without limiting the foregoing, Sahas Labs does not warrant that the
              Service will meet your requirements, that it will be uninterrupted,
              secure, timely, or error-free, that any defects will be corrected,
              or that the data or results obtained through the Service will be
              accurate, complete, or reliable. Any reliance you place on the
              Service is at your own risk.
            </p>
          </Section>

          <Section id="liability" title="13. Limitation of liability">
            <p>
              To the maximum extent permitted by applicable law, in no event will
              Sahas Labs, its affiliates, or their respective directors,
              officers, employees, agents, or licensors be liable for any
              indirect, incidental, special, consequential, exemplary, or punitive
              damages, or for any loss of profits, revenue, goodwill, data, or
              business, business interruption, production or output losses,
              equipment damage, or downtime, arising out of or relating to these
              Terms or the Service, whether based on contract, tort (including
              negligence), strict liability, or any other theory, and whether or
              not Sahas Labs has been advised of the possibility of such damages.
            </p>
            <p>
              To the maximum extent permitted by applicable law, the total
              aggregate liability of Sahas Labs arising out of or relating to
              these Terms or the Service will not exceed the greater of (a) the
              total amount of fees actually paid by you to Sahas Labs for the
              Service in the twelve (12) months immediately preceding the event
              giving rise to the liability, or (b) {LIABILITY_CAP} if no fees were
              paid. The limitations and exclusions in this section apply to the
              fullest extent permitted by law but do not exclude or limit any
              liability that cannot be excluded or limited under applicable law.
            </p>
          </Section>

          <Section id="indemnification" title="14. Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless Sahas Labs, its
              affiliates, and their respective directors, officers, employees, and
              agents from and against any and all claims, demands, actions,
              liabilities, damages, losses, costs, and expenses (including
              reasonable legal fees) arising out of or relating to: (a) your
              access to or use of the Service; (b) your Customer Data, including
              any claim that it infringes or violates the rights of a third party
              or any law; (c) your violation of these Terms or of any applicable
              law; or (d) your misuse of the Service. We will provide you with
              reasonable notice of any such claim and may participate in its
              defence.
            </p>
          </Section>

          <Section id="termination" title="15. Term, suspension & termination">
            <p>
              These Terms remain in effect for as long as you use the Service.
              Either party may terminate these Terms and your access to the
              Service at any time. We may suspend, restrict, or terminate your
              access to the Service, in whole or in part, immediately and without
              liability, if we reasonably believe that you have breached these
              Terms, that your use poses a security, legal, or operational risk,
              or that suspension or termination is required by law.
            </p>
            <p>
              Upon termination, your right to access and use the Service ceases.
              Where reasonably feasible and not prohibited by law, we will provide
              a reasonable window during which you may export your Customer Data
              before it is deleted; after that window, we may delete or anonymise
              Customer Data in accordance with our Privacy Policy and retention
              practices. Provisions that by their nature should survive
              termination — including those relating to Customer Data ownership,
              intellectual property, confidentiality, disclaimers, limitation of
              liability, indemnification, governing law, and these general
              provisions — will survive.
            </p>
          </Section>

          <Section
            id="changes"
            title="16. Changes to the Service & these Terms"
          >
            <p>
              We may modify the Service and these Terms from time to time. When we
              make material changes to these Terms, we will update the &ldquo;Last
              updated&rdquo; date above and, where appropriate, provide notice
              through the Service or by other reasonable means. Your continued
              access to or use of the Service after the updated Terms take effect
              constitutes your acceptance of the changes. If you do not agree to
              the updated Terms, you must stop using the Service.
            </p>
          </Section>

          <Section
            id="governing-law"
            title="17. Governing law & dispute resolution"
          >
            <p>
              These Terms and any dispute or claim arising out of or in connection
              with them or the Service, including any non-contractual disputes or
              claims, are governed by and construed in accordance with the laws of
              India, without regard to its conflict-of-laws principles. Subject to
              the parties&rsquo; obligation to first attempt resolution in good
              faith, the courts located at the registered seat of Sahas Labs Pvt
              Ltd in India will have exclusive jurisdiction over any dispute
              arising out of or relating to these Terms or the Service.
            </p>
            <p>
              Before initiating any formal proceedings, the parties will use
              reasonable efforts to resolve the dispute amicably through
              good-faith discussions, by sending written notice of the dispute to
              the other party.
            </p>
          </Section>

          <Section id="force-majeure" title="18. Force majeure">
            <p>
              Sahas Labs will not be liable for any failure or delay in performing
              its obligations under these Terms to the extent that such failure or
              delay is caused by events beyond its reasonable control, including
              acts of God, natural disasters, fire, flood, epidemic or pandemic,
              war, terrorism, civil unrest, governmental action, labour disputes,
              failures or interruptions of utilities, telecommunications, or
              Internet services, and failures of third-party providers or
              infrastructure.
            </p>
          </Section>

          <Section id="general" title="19. General">
            <p>
              These Terms, together with the Privacy Policy and any order or plan
              you agree to, constitute the entire agreement between you and Sahas
              Labs with respect to the Service, and supersede all prior or
              contemporaneous understandings on that subject. If any provision of
              these Terms is held to be invalid or unenforceable, that provision
              will be limited or eliminated to the minimum extent necessary, and
              the remaining provisions will remain in full force and effect.
            </p>
            <p>
              Our failure to enforce any right or provision of these Terms will not
              be deemed a waiver of that right or provision. You may not assign or
              transfer these Terms, by operation of law or otherwise, without our
              prior written consent; Sahas Labs may assign or transfer these Terms,
              in whole or in part, without restriction, including in connection
              with a merger, acquisition, or sale of assets. Notices to Sahas Labs
              under these Terms should be sent to{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="contact" title="20. Contact">
            <p>
              If you have any questions about these Terms or the Service, please
              contact us at:
            </p>
            <div
              data-pdf-block
              className="rounded-xl border border-border bg-surface-elevated/60 p-5 text-sm leading-relaxed text-on-surface-muted"
            >
              <p className="font-medium text-on-surface">Sahas Labs Pvt Ltd</p>
              <p>
                Email:{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-medium text-brand hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
            <p className="text-xs text-on-surface-muted">
              These Terms of Service are effective as of {EFFECTIVE_DATE} and are
              governed by the laws of India.
            </p>
          </Section>
        </div>

        {/* Footer nav */}
        <div
          data-no-print
          className="mt-4 flex flex-col items-start justify-between gap-3 border-t border-border pt-6 text-sm text-on-surface-muted sm:flex-row sm:items-center"
        >
          <span>
            © {new Date().getFullYear()} Sahas Labs Pvt Ltd. All rights
            reserved.
          </span>
          <div className="flex items-center gap-4">
            <Link className="hover:text-on-surface" to="/privacy">
              Privacy Policy
            </Link>
            <Link className="hover:text-on-surface" to="/">
              Home
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
